/**
 * Life Blueprint V2.9.9 — Migration Batch 02 (Sheet27: B42 / B43 / B44)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * STRICT SCOPE
 *   This file implements ONLY the three audited Sheet27 cells B42, B43, B44.
 *   No other Sheet27 cells (B45, B46, B47, ...) are implemented here.
 *   No downstream Sheet28/29/30 logic. No upstream generation of B13, B34, or
 *   E19..E30 — those arrive as validated upstream Golden Master inputs.
 *
 * GOLDEN MASTER PROVENANCE (audited)
 *   Sheet27!B42
 *     =IF($B$13>=E30,1,
 *      IF($B$13>=E29,12,
 *      IF($B$13>=E28,11,
 *      IF($B$13>=E27,10,
 *      IF($B$13>=E26,9,
 *      IF($B$13>=E25,8,
 *      IF($B$13>=E24,7,
 *      IF($B$13>=E23,6,
 *      IF($B$13>=E22,5,
 *      IF($B$13>=E21,4,
 *      IF($B$13>=E20,3,
 *      IF($B$13>=E19,2,1))))))))))))
 *
 *   Sheet27!B43  =MOD(MOD(B34-1,5)*2+2,10)+1
 *   Sheet27!B44  =MOD(B43-1+MOD(B42-3+12,12),10)+1
 *
 *   Audited values:
 *     B34 = 4          → B43 = 9
 *     B42 = 3, B43 = 9 → B44 = 9
 *
 * INPUT CONTRACT (Batch 02 only — NOT a final public API)
 *   Field names are intentionally suffixed with their Excel cell / range id.
 *
 *     upstreamDateTime_B13        : Date         (Sheet27!$B$13)
 *     upstreamValue_B34           : integer 1..10 (Sheet27!B34, migrated Batch 01)
 *     upstreamBoundaries_E19_E30  : { E19..E30: Date } (Sheet27!E19:E30)
 *     upstreamValue_B42_audited   : integer 1..12 (optional, when E19..E30 not supplied)
 *
 *   E19..E30 semantics and generation are NOT reconstructed here — they arrive
 *   as validated upstream boundary values only.
 *
 * BUSINESS MEANING
 *   The business meaning of B42, B43 and B44 is deliberately NOT labelled in
 *   this module because that meaning has not been independently evidenced.
 *   Do NOT rename these fields to any Chinese-metaphysics label (月柱/日柱/…)
 *   until the Golden Master audit confirms the semantics.
 *
 * DETERMINISM
 *   No randomness, no wall clock. Given identical input this module returns
 *   byte-wise identical output on every run.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { excelMod } from "@/engine/v299/batch01_sheet27";

const E_KEYS_DESC = [
  // Excel formula order: E30 first, ..., E19 last. Corresponding outputs:
  ["E30", 1],
  ["E29", 12],
  ["E28", 11],
  ["E27", 10],
  ["E26", 9],
  ["E25", 8],
  ["E24", 7],
  ["E23", 6],
  ["E22", 5],
  ["E21", 4],
  ["E20", 3],
  ["E19", 2],
];

/**
 * Excel-compatible port of Sheet27!B42.
 *
 * Preserves the exact nested-IF ordering (E30 tested first, E19 tested last,
 * fallthrough returns 1) and inclusive `>=` comparison against Excel datetime
 * serial numbers (represented here as `Date.getTime()` milliseconds).
 *
 * @param {Date} upstreamDateTime_B13
 * @param {{E19:Date,E20:Date,E21:Date,E22:Date,E23:Date,E24:Date,E25:Date,E26:Date,E27:Date,E28:Date,E29:Date,E30:Date}} upstreamBoundaries_E19_E30
 * @returns {number} integer in [1, 12]
 */
export function computeB42(upstreamDateTime_B13, upstreamBoundaries_E19_E30) {
  if (!(upstreamDateTime_B13 instanceof Date) || isNaN(upstreamDateTime_B13.getTime())) {
    throw new TypeError("[Batch02·B42] upstreamDateTime_B13 must be a valid Date");
  }
  if (upstreamBoundaries_E19_E30 === null || typeof upstreamBoundaries_E19_E30 !== "object") {
    throw new TypeError("[Batch02·B42] upstreamBoundaries_E19_E30 must be an object");
  }
  const t = upstreamDateTime_B13.getTime();
  for (const [key, out] of E_KEYS_DESC) {
    const d = upstreamBoundaries_E19_E30[key];
    if (!(d instanceof Date) || isNaN(d.getTime())) {
      throw new TypeError(
        `[Batch02·B42] upstreamBoundaries_E19_E30.${key} must be a valid Date`
      );
    }
    // Excel comparison is inclusive `>=`.
    if (t >= d.getTime()) return out;
  }
  return 1; // Excel fallthrough
}

/**
 * Excel-compatible port of Sheet27!B43.
 *   B43 = MOD( MOD(B34 - 1, 5) * 2 + 2, 10 ) + 1
 * @param {number} upstreamValue_B34
 * @returns {number} integer in [1, 10]
 */
export function computeB43(upstreamValue_B34) {
  if (!Number.isInteger(upstreamValue_B34)) {
    throw new TypeError("[Batch02·B43] upstreamValue_B34 must be an integer");
  }
  return excelMod(excelMod(upstreamValue_B34 - 1, 5) * 2 + 2, 10) + 1;
}

/**
 * Excel-compatible port of Sheet27!B44.
 *   B44 = MOD( B43 - 1 + MOD(B42 - 3 + 12, 12), 10 ) + 1
 *
 * The inner `+ 12` inside the Excel formula guarantees a non-negative
 * argument to the inner MOD for B42 ∈ [1, 12]. `excelMod` handles negatives
 * defensively anyway.
 *
 * @param {number} upstreamValue_B42
 * @param {number} upstreamValue_B43
 * @returns {number} integer in [1, 10]
 */
export function computeB44(upstreamValue_B42, upstreamValue_B43) {
  if (!Number.isInteger(upstreamValue_B42)) {
    throw new TypeError("[Batch02·B44] upstreamValue_B42 must be an integer");
  }
  if (!Number.isInteger(upstreamValue_B43)) {
    throw new TypeError("[Batch02·B44] upstreamValue_B43 must be an integer");
  }
  return excelMod(
    upstreamValue_B43 - 1 + excelMod(upstreamValue_B42 - 3 + 12, 12),
    10
  ) + 1;
}

/**
 * Aggregate entry point for Batch 02.
 *
 * B42 is computed from the boundary array when `upstreamBoundaries_E19_E30`
 * is supplied. If it is not supplied (e.g. Golden Master evidence for
 * E19..E30 is not yet available), the caller must pass
 * `upstreamValue_B42_audited` — the audited upstream value — instead.
 *
 * @param {{
 *   upstreamDateTime_B13?: Date,
 *   upstreamBoundaries_E19_E30?: Object,
 *   upstreamValue_B34: number,
 *   upstreamValue_B42_audited?: number
 * }} slice
 * @returns {{
 *   B42: number, B43: number, B44: number,
 *   _provenance: Object
 * }}
 */
export function computeBatch02Sheet27(slice) {
  if (slice === null || typeof slice !== "object") {
    throw new TypeError("[Batch02] slice must be an object");
  }
  const {
    upstreamDateTime_B13,
    upstreamBoundaries_E19_E30,
    upstreamValue_B34,
    upstreamValue_B42_audited,
  } = slice;

  const B43 = computeB43(upstreamValue_B34);

  let B42;
  let b42Source;
  if (upstreamBoundaries_E19_E30 !== undefined) {
    if (upstreamDateTime_B13 === undefined) {
      throw new TypeError(
        "[Batch02] upstreamDateTime_B13 required when boundaries are supplied"
      );
    }
    B42 = computeB42(upstreamDateTime_B13, upstreamBoundaries_E19_E30);
    b42Source = "COMPUTED_FROM_BOUNDARIES";
  } else {
    if (!Number.isInteger(upstreamValue_B42_audited)) {
      throw new TypeError(
        "[Batch02] Either upstreamBoundaries_E19_E30 (with upstreamDateTime_B13) " +
          "OR upstreamValue_B42_audited must be supplied."
      );
    }
    B42 = upstreamValue_B42_audited;
    b42Source = "AUDITED_UPSTREAM";
  }

  const B44 = computeB44(B42, B43);

  return {
    B42,
    B43,
    B44,
    _provenance: {
      excelSources: ["Sheet27!B42", "Sheet27!B43", "Sheet27!B44"],
      formulas: {
        "Sheet27!B42":
          "=IF($B$13>=E30,1,IF($B$13>=E29,12,IF($B$13>=E28,11,IF($B$13>=E27,10," +
          "IF($B$13>=E26,9,IF($B$13>=E25,8,IF($B$13>=E24,7,IF($B$13>=E23,6," +
          "IF($B$13>=E22,5,IF($B$13>=E21,4,IF($B$13>=E20,3,IF($B$13>=E19,2,1))))))))))))",
        "Sheet27!B43": "=MOD(MOD(B34-1,5)*2+2,10)+1",
        "Sheet27!B44": "=MOD(B43-1+MOD(B42-3+12,12),10)+1",
      },
      inputCitations: {
        upstreamDateTime_B13: "Sheet27!$B$13",
        upstreamBoundaries_E19_E30: "Sheet27!E19:E30",
        upstreamValue_B34: "Sheet27!B34 (migrated Batch 01)",
      },
      b42Source, // "COMPUTED_FROM_BOUNDARIES" | "AUDITED_UPSTREAM"
      note:
        "Business meaning of B42/B43/B44 is intentionally NOT labelled; no " +
        "independent evidence has established their semantics yet. E19..E30 " +
        "semantics/generation are not reconstructed here.",
    },
  };
}
